// components/LSSF/DonationsList.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdDelete, MdSearch, MdVisibility, MdReceipt } from 'react-icons/md';
import { Link } from 'react-router-dom';

const DonationsList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const limit = 20;

  const getDonations = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/donation`);
      setData(res.data.data);
      setFilteredData(res.data.data);
      const total = Math.ceil(res.data.count / limit);
      setTotalPages(total > 0 ? total : 1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching donations:', err);
      toast.error('Failed to fetch donations');
      setLoading(false);
    }
  };

  useEffect(() => {
    getDonations();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.filter(item => {
        return (
          (item.donor_name && item.donor_name.toLowerCase().includes(searchLower)) ||
          (item.receipt_no && item.receipt_no.toLowerCase().includes(searchLower)) ||
          (item.email && item.email.toLowerCase().includes(searchLower)) ||
          (item.mobile && item.mobile.toString().includes(searchLower)) ||
          (item.purpose && item.purpose.toLowerCase().includes(searchLower)) ||
          (item.payment_mode && item.payment_mode.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const deleteDonation = async (id) => {
    const choice = window.confirm('Are you sure you want to delete this donation record?');
    if (choice) {
      try {
        await axios.delete(`${base_url}/api/v1/donations/${id}`);
        toast.success('Donation deleted successfully');
        getDonations();
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete donation');
      }
    }
  };

  const viewDetails = (donation) => {
    setSelectedDonation(donation);
    setShowModal(true);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-4">
                  <h4 className="mb-0">Donations</h4>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, receipt, email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <button className="btn btn-success btn-sm" onClick={() => window.print()}>
                    <MdReceipt /> Export Report
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Receipt No.</th>
                        <th>Donor Name</th>
                        <th>Mobile</th>
                        <th>Email</th>
                        <th>Amount</th>
                        <th>Purpose</th>
                        <th>Payment Mode</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          {[...Array(10)].map((_, i) => (
                            <td key={i}><Skeleton count={1} /></td>
                          ))}
                        </tr>
                      ) : paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>
                              <strong>{item.receipt_no}</strong>
                            </td>
                            <td>{item.donor_name}</td>
                            <td>{item.mobile}</td>
                            <td>{item.email}</td>
                            <td className="text-success fw-bold">{formatAmount(item.amount)}</td>
                            <td>{item.purpose}</td>
                            <td>
                              <span className={`badge ${item.payment_mode === 'Online' ? 'bg-success' : 'bg-info'}`}>
                                {item.payment_mode || 'Online'}
                              </span>
                            </td>
                            <td>{new Date(item.created_at).toLocaleDateString()}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-info me-2"
                                onClick={() => viewDetails(item)}
                              >
                                <MdVisibility size={18} /> View
                              </button>
                              {/* <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteDonation(item.id)}
                              >
                                <MdDelete size={18} /> Delete
                              </button> */}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger">
                          <td colSpan={10}>No donations found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Summary Section */}
                {!loading && filteredData.length > 0 && (
                  <div className="row mt-4">
                    <div className="col-md-12">
                      <div className="alert alert-info">
                        <strong>Summary:</strong> Total Donations: {filteredData.length} | 
                        Total Amount: {formatAmount(filteredData.reduce((sum, item) => sum + parseFloat(item.amount), 0))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pagination Controls */}
                {filteredData.length > limit && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Viewing Donation Details */}
      {showModal && selectedDonation && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">Donation Details - Receipt #{selectedDonation.receipt_no}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Receipt Number:</strong>
                    <p>{selectedDonation.receipt_no}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Donor Name:</strong>
                    <p>{selectedDonation.donor_name}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Mobile:</strong>
                    <p>{selectedDonation.mobile}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Email:</strong>
                    <p>{selectedDonation.email}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Amount:</strong>
                    <p className="text-success fw-bold">{formatAmount(selectedDonation.amount)}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Purpose:</strong>
                    <p>{selectedDonation.purpose}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Payment Mode:</strong>
                    <p>{selectedDonation.payment_mode || 'Online'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Donation Date:</strong>
                    <p>{new Date(selectedDonation.created_at).toLocaleString()}</p>
                  </div>
                  {selectedDonation.razorpay_payment_id && (
                    <>
                      <div className="col-md-6 mb-3">
                        <strong>Razorpay Payment ID:</strong>
                        <p><small>{selectedDonation.razorpay_payment_id}</small></p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <strong>Razorpay Order ID:</strong>
                        <p><small>{selectedDonation.razorpay_order_id}</small></p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                  Print Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default DonationsList;